/**
 * Server clock estimation (NTP-style).
 *
 * A sample is one request/response exchange: client send time t0, server time ts, client receive
 * time t1. offset = ts − (t0 + t1)/2, rtt = t1 − t0. We keep the last 8 samples and trust the one
 * with the lowest RTT (least queueing delay); its uncertainty is rtt/2.
 *
 * Client times use a monotonic clock anchored to the wall clock at load, so a user changing the
 * device clock mid-mission does not corrupt the estimate.
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import type { EpochMs } from '@apos/shared';

export interface ClockSample {
  /** client send (monotonic-wall ms) */
  t0: number;
  /** server time */
  ts: number;
  /** client receive */
  t1: number;
  offset: number;
  rtt: number;
  /** when the sample was taken (client ms), for age */
  at: number;
}

export interface ClockEstimate {
  offsetMs: number;
  uncertaintyMs: number;
  rttMs: number;
  sampleCount: number;
  /** client time of the chosen sample */
  sampledAt: number;
}

export const MAX_SAMPLES = 8;

export function makeSample(t0: number, ts: number, t1: number): ClockSample | null {
  if (!Number.isFinite(t0) || !Number.isFinite(ts) || !Number.isFinite(t1)) return null;
  const rtt = t1 - t0;
  if (rtt < 0 || rtt > 30_000) return null;
  return { t0, ts, t1, rtt, offset: ts - (t0 + t1) / 2, at: t1 };
}

/** Best-of-N: lowest RTT sample wins. Ties go to the newest sample. */
export function estimateOffset(samples: readonly ClockSample[]): ClockEstimate | null {
  let best: ClockSample | null = null;
  for (const s of samples) {
    if (!best || s.rtt < best.rtt || (s.rtt === best.rtt && s.at > best.at)) best = s;
  }
  if (!best) return null;
  return {
    offsetMs: best.offset,
    uncertaintyMs: best.rtt / 2,
    rttMs: best.rtt,
    sampleCount: samples.length,
    sampledAt: best.at,
  };
}

// Monotonic wall clock: Date.now() at module load + performance.now() delta.
const hasPerf = typeof performance !== 'undefined' && typeof performance.now === 'function';
const anchorWall = Date.now();
const anchorPerf = hasPerf ? performance.now() : 0;
export function clientNow(): number {
  return hasPerf ? anchorWall + (performance.now() - anchorPerf) : Date.now();
}

type Listener = () => void;

export class ClockSync {
  private samples: ClockSample[] = [];
  private est: ClockEstimate | null = null;
  private listeners = new Set<Listener>();

  addSample(t0: number, ts: number, t1: number): void {
    const s = makeSample(t0, ts, t1);
    if (!s) return;
    this.samples.push(s);
    if (this.samples.length > MAX_SAMPLES) this.samples.splice(0, this.samples.length - MAX_SAMPLES);
    this.est = estimateOffset(this.samples);
    this.emit();
  }

  /** Coarse one-way hint (e.g. WS `hello`) used only until a round-trip sample exists. */
  hint(serverTime: EpochMs): void {
    if (this.samples.length > 0) return;
    const now = clientNow();
    this.est = { offsetMs: serverTime - now, uncertaintyMs: 1000, rttMs: 2000, sampleCount: 0, sampledAt: now };
    this.emit();
  }

  reset(): void {
    this.samples = [];
    this.est = null;
    this.emit();
  }

  get estimate(): ClockEstimate | null {
    return this.est;
  }

  get offsetMs(): number {
    return this.est?.offsetMs ?? 0;
  }

  get uncertaintyMs(): number | null {
    return this.est ? this.est.uncertaintyMs : null;
  }

  now(): EpochMs {
    return clientNow() + this.offsetMs;
  }

  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): ClockEstimate | null => this.est;

  private emit(): void {
    for (const l of this.listeners) l();
  }
}

export const clock = new ClockSync();

/** Best estimate of the server (mission authority) clock, epoch ms. */
export function serverNow(): EpochMs {
  return clock.now();
}

/** Take N quick samples via `GET /api/time` (used before the WebSocket is up). */
export async function syncViaHttp(n = 5, signal?: AbortSignal): Promise<ClockEstimate | null> {
  for (let i = 0; i < n; i++) {
    if (signal?.aborted) break;
    const t0 = clientNow();
    try {
      const res = await fetch('/api/time', { credentials: 'include', signal, cache: 'no-store' });
      const t1 = clientNow();
      if (!res.ok) break;
      const body = (await res.json()) as { serverTime: number };
      clock.addSample(t0, body.serverTime, t1);
    } catch {
      break;
    }
  }
  return clock.estimate;
}

/** Re-renders every `intervalMs` with the current server time. */
export function useServerNow(intervalMs = 1000): EpochMs {
  const [now, setNow] = useState(() => serverNow());
  useEffect(() => {
    let raf = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = () => {
      setNow(serverNow());
      if (intervalMs < 50) {
        raf = requestAnimationFrame(tick);
      } else {
        // align second-ish updates to the wall clock so displayed seconds tick together
        const delay = intervalMs >= 1000 ? intervalMs - (serverNow() % intervalMs) + 5 : intervalMs;
        timer = setTimeout(tick, delay);
      }
    };
    tick();
    return () => {
      cancelAnimationFrame(raf);
      if (timer) clearTimeout(timer);
    };
  }, [intervalMs]);
  return now;
}

/** Current clock estimate (offset ± uncertainty) for status indicators. */
export function useClockEstimate(): ClockEstimate | null {
  return useSyncExternalStore(clock.subscribe, clock.getSnapshot, clock.getSnapshot);
}
